import axios from "axios";

// Resolve the API base URL.
//
//   - `REACT_APP_BACKEND_URL` is baked in at build time. In Emergent preview
//     it points at the preview domain; in single-service production builds
//     it's empty (the SPA + API share one origin).
//   - But auto-deploy can sometimes bake in the emergent.host URL even when
//     the SPA is being served from a custom domain (e.g. letsallsingtogether.com).
//     When that happens cross-origin XHRs blow up with CORS errors and every
//     page falls back to its hardcoded defaults.
//   - Defence: if the served origin differs from the baked URL, prefer the
//     current origin. The backend lives at /api on the same host in single-
//     service mode, so relative URLs Just Work.
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const _resolveBackendUrl = () => {
  if (typeof window === "undefined") return ENV_BACKEND_URL;
  if (!ENV_BACKEND_URL) return ""; // already same-origin
  try {
    const envHost = new URL(ENV_BACKEND_URL).host;
    const winHost = window.location.host;
    // Different host than the build was wired for → use the current origin.
    if (envHost && winHost && envHost !== winHost) return "";
  } catch (_) {
    /* malformed env URL — fall through to env value */
  }
  return ENV_BACKEND_URL;
};

const BACKEND_URL = _resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
export const ASSET_BASE = BACKEND_URL;

export const api = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

// ---- Auto-refresh on 401 ----------------------------------------
// Backend issues a short-lived access token (12h) + a long-lived refresh
// token (30d). When the access token expires mid-task (e.g. you're
// drafting a long post), the very next admin call returns 401 and the
// UI shows "not authenticated". The interceptor below catches that 401
// once, calls /auth/refresh (which mints a new access cookie from the
// still-valid refresh cookie), and retries the original request — so
// the user never sees the error unless their refresh token is ALSO
// expired (genuinely signed out for 30+ days).
//
// Notes:
//   • Endpoints excluded from refresh: /auth/login, /auth/refresh, /auth/me.
//     These either don't need an access token or are part of the refresh
//     flow itself — retrying them would loop.
//   • Concurrent 401s share a single in-flight refresh Promise to avoid
//     stampeding the server with parallel refresh calls.

let _refreshInFlight = null;

const AUTH_EXEMPT = ["/auth/login", "/auth/refresh", "/auth/me", "/auth/logout"];

const isAuthEndpoint = (cfg) =>
  AUTH_EXEMPT.some((p) => (cfg?.url || "").includes(p));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status !== 401 ||
      !original ||
      original._retried ||
      isAuthEndpoint(original)
    ) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      if (!_refreshInFlight) {
        _refreshInFlight = api.post("/auth/refresh").finally(() => {
          _refreshInFlight = null;
        });
      }
      await _refreshInFlight;
      return api.request(original);
    } catch (refreshErr) {
      // Refresh failed too — let the original 401 surface so AuthContext can
      // bounce the user back to /admin/login cleanly.
      return Promise.reject(error);
    }
  },
);

// Feeds
export const getSources = () => api.get("/sources").then((r) => r.data);
export const getChannels = () => api.get("/channels").then((r) => r.data);
export const getNews = (source = "all") =>
  api.get("/news", { params: { source } }).then((r) => r.data);
export const getVideos = (channel = "all") =>
  api.get("/videos", { params: { channel } }).then((r) => r.data);
export const getTable = (competition = "PL") =>
  api.get("/table", { params: { competition } }).then((r) => r.data);
export const getMatches = () => api.get("/matches").then((r) => r.data);

// Auth
export const login = (email, password, remember = false) =>
  api.post("/auth/login", { email, password, remember }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const me = () => api.get("/auth/me").then((r) => r.data);

// Posts (public)
export const getPosts = (section = "home", q = "") =>
  api
    .get("/posts", { params: { section, ...(q ? { q } : {}) } })
    .then((r) => r.data);
export const getPost = (id) => api.get(`/posts/${id}`).then((r) => r.data);
// Admin-only — fetches a post regardless of published state or scheduled
// publish time, so the editor can re-open drafts and scheduled posts.
export const getAdminPost = (id) =>
  api.get(`/admin/posts/${id}`).then((r) => r.data);

// Site-wide search across posts + custom pages
export const siteSearch = (q) =>
  api.get("/search", { params: { q } }).then((r) => r.data);

// Posts (admin)
export const adminListPosts = () => api.get("/admin/posts").then((r) => r.data);
export const createPost = (payload) =>
  api.post("/posts", payload).then((r) => r.data);
export const updatePost = (id, payload) =>
  api.put(`/posts/${id}`, payload).then((r) => r.data);
export const deletePost = (id) =>
  api.delete(`/posts/${id}`).then((r) => r.data);

// Nav-item click tracking — fire-and-forget. Powers the home rotating tiles'
// "yesterday's most-clicked" leading slot. Never throws.
export const trackNavClick = (id) => {
  if (!id) return;
  try {
    api.post(`/nav-items/${id}/click`).catch(() => {});
  } catch (_) {
    /* non-fatal */
  }
};

// Uploads
export const uploadImage = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

// Given an URL returned by /api/uploads (like "/api/files/..."), resolve it to an absolute URL
export const resolveAsset = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${BACKEND_URL}${path}`;
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
